import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CourseCard } from "@/components/learner/CourseCard";
import { FORMAT_LABELS, errMsg, useApi } from "@/lib/api";

const ALL = "all";
const DURATION_LABELS = { short: "Under 5 hours", medium: "5–20 hours", long: "Over 20 hours" };

function FilterSelect({ id, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-44" data-testid={`filter-${id}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`filter-${id}-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Library() {
  const api = useApi();
  const [filters, setFilters] = useState({ skills: [], levels: [], formats: [], durations: [] });
  const [f, setF] = useState({ q: "", skill: ALL, level: ALL, format: ALL, duration: ALL, price: ALL });
  const [courses, setCourses] = useState(null);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/courses/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== ALL));
      api.get("/courses", { params }).then((r) => setCourses(r.data)).catch((e) => setErr(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [api, f]);

  const active = Object.entries(f).some(([k, v]) => (k === "q" ? v : v !== ALL));
  return (
    <div>
      <PageHeader eyebrow="Learn" title="Course library" />
      <div className="mb-8 space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search courses or skills" className="pl-9" maxLength={80} data-testid="course-search-input" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <FilterSelect id="skill" value={f.skill} onChange={(v) => set("skill", v)} placeholder="All skills" options={filters.skills.map((s) => [s, s])} />
          <FilterSelect id="level" value={f.level} onChange={(v) => set("level", v)} placeholder="All levels" options={filters.levels.map((s) => [s, s[0].toUpperCase() + s.slice(1)])} />
          <FilterSelect id="format" value={f.format} onChange={(v) => set("format", v)} placeholder="All formats" options={filters.formats.map((s) => [s, FORMAT_LABELS[s] || s])} />
          <FilterSelect id="duration" value={f.duration} onChange={(v) => set("duration", v)} placeholder="Any duration" options={filters.durations.map((s) => [s, DURATION_LABELS[s]])} />
          <FilterSelect id="price" value={f.price} onChange={(v) => set("price", v)} placeholder="Free & paid" options={[["free", "Free"], ["paid", "Paid"]]} />
          {active && <Button variant="ghost" onClick={() => setF({ q: "", skill: ALL, level: ALL, format: ALL, duration: ALL, price: ALL })} data-testid="clear-filters-btn"><X className="mr-1 h-4 w-4" /> Clear</Button>}
        </div>
      </div>
      {err && <p className="text-rose-600">{err}</p>}
      {!courses ? <Spinner /> : courses.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="no-courses-message">No courses match these filters.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="course-grid">
          {courses.map((c) => <CourseCard key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}
